import type { TimePeriodPresets } from '@hygge/shared';
import { toDateKey, toDateParam } from '@/lib/date';

/**
 * Pure shaping for the Time Tracker dashboard.
 *
 * Everything here is a function of its arguments so it can be pinned by tests —
 * the period maths in particular, which is the one part of the screen that can
 * be silently, plausibly wrong.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/** Beyond this many days the line gets a point per ISO week instead of per day. */
const DAY_AXIS_MAX_DAYS = 31;

/**
 * The longest window the endpoint will serve. Mirrored here only so snapping
 * out to whole weeks cannot turn a request it would have accepted into a 400.
 */
const MAX_WINDOW_DAYS = 400;

export type PeriodPreset = 'this-month' | 'last-month' | 'this-week' | 'last-week' | 'custom';

/** An inclusive window as ISO instants, ready to send to the summary endpoint. */
export interface DateWindow {
  from: string;
  to: string;
}

/**
 * The window for a run of calendar days, as whole UTC days.
 *
 * This is the conversion the dashboard exists to get right. The endpoint's day
 * axis buckets `started_at` by UTC calendar day, so a window built the obvious
 * way — local midnight through `.toISOString()` — hands it boundaries in the
 * middle of a UTC day. From Kyiv, "This month" for March
 * would start at 2026-02-28T22:00:00Z and the chart would grow a February
 * bucket holding March's work.
 *
 * Sending UTC-day edges costs a couple of hours of drift against local midnight
 * for the people on either edge of the range, and buys an axis whose labels
 * mean what they say. For a screen answering "where did the period's time go",
 * that is the right trade.
 */
export function utcDayWindow(fromDay: string, toDay: string): DateWindow {
  return { from: `${fromDay}T00:00:00.000Z`, to: `${toDay}T23:59:59.999Z` };
}

/**
 * A preset's window, ready to send: the days the COMPANY calendar names for it
 * (`GET /time-entries/periods`), as whole UTC-digit days.
 *
 * Not worked out on the reader's clock: a screen that resolved it locally
 * would name a different month than the Reports summary, which runs on these
 * same days, for everyone west or east of the company at a month's edge. Null
 * until that answer has arrived, and for `custom`, which has no maths.
 * [HW-929]
 */
export function presetRange(preset: PeriodPreset, periods: TimePeriodPresets | undefined): DateWindow | null {
  if (preset === 'custom' || !periods) return null;
  const days = periods.presets[preset];
  return utcDayWindow(days.from, days.to);
}

/**
 * The window for two picker dates. `DatePicker` returns LOCAL midnight, so the
 * day the user clicked is read off the local calendar and then sent as a whole
 * UTC day. Null when either end is missing or the range is inverted.
 */
export function customRange(from: Date | null, to: Date | null): DateWindow | null {
  const fromDay = toDateParam(from);
  const toDay = toDateParam(to);
  if (!fromDay || !toDay || fromDay > toDay) return null;
  return utcDayWindow(fromDay, toDay);
}

/**
 * A point per day, or per ISO week once the range grows past a month — 90 daily
 * points on a card-width chart is a texture, not a trend.
 */
export function axisMode(from: string, to: string): 'day' | 'week' {
  const fromKey = toDateKey(from);
  const toKey = toDateKey(to);
  if (!fromKey || !toKey) return 'day';
  const span = Date.parse(`${toKey}T00:00:00.000Z`) - Date.parse(`${fromKey}T00:00:00.000Z`);
  if (!Number.isFinite(span)) return 'day';
  const days = Math.round(span / DAY_MS) + 1; // inclusive of both ends
  return days > DAY_AXIS_MAX_DAYS ? 'week' : 'day';
}

// ─── Whole-week windows ───────────────────────────────────────────────────

/** Days between two `yyyy-MM-dd` keys, counting both ends. */
function spanDays(fromDay: string, toDay: string): number {
  const span = Date.parse(`${toDay}T00:00:00.000Z`) - Date.parse(`${fromDay}T00:00:00.000Z`);
  return Math.round(span / DAY_MS) + 1;
}

function shiftDayKey(dayKey: string, days: number): string {
  return new Date(Date.parse(`${dayKey}T00:00:00.000Z`) + days * DAY_MS).toISOString().slice(0, 10);
}

/** Days since Monday: 0 for Monday, 6 for Sunday. */
function sinceMonday(dayKey: string): number {
  return (new Date(`${dayKey}T00:00:00.000Z`).getUTCDay() + 6) % 7;
}

/**
 * A window grown outwards to whole ISO weeks.
 *
 * Weekly buckets are keyed by their Monday, so a window starting on a Sunday
 * hands that one day to the Monday *before* the period: a bucket holding a
 * single day next to bucket holding seven. On a team working perfectly steadily
 * that draws as a cliff at each end and labels a week the reader never asked
 * for. Widening the query instead of trimming the chart keeps every bucket a
 * real seven days AND keeps the headline totals describing exactly the days the
 * line is drawn from.
 */
export function wholeWeekWindow(window: DateWindow): DateWindow {
  const fromKey = toDateKey(window.from);
  const toKey = toDateKey(window.to);
  if (!fromKey || !toKey) return window;

  let start = shiftDayKey(fromKey, -sinceMonday(fromKey));
  const end = shiftDayKey(toKey, 6 - sinceMonday(toKey));
  // Widening can push a long custom range past what the endpoint will serve.
  // Whole weeks come off the oldest end — a shorter answer beats an error, and
  // the period actually queried is printed on the screen either way.
  while (spanDays(start, end) > MAX_WINDOW_DAYS) start = shiftDayKey(start, 7);

  return utcDayWindow(start, end);
}

/** A window paired with the axis it will be drawn on. */
export interface SummaryWindow extends DateWindow {
  mode: 'day' | 'week';
}

/**
 * The window actually queried, and the axis for it — the single place the two
 * decisions are made together, because a weekly axis changes which days have to
 * be fetched.
 */
export function summaryWindow(requested: DateWindow): SummaryWindow {
  const mode = axisMode(requested.from, requested.to);
  if (mode === 'day') return { ...requested, mode };
  return { ...wholeWeekWindow(requested), mode };
}

/**
 * The head of a ranked list plus what it leaves out, so the screen can say
 * "and 14 more" instead of pretending the tail does not exist.
 */
export function topWithRest<T extends { totalSeconds: number }>(
  rows: T[],
  n: number,
): { shown: T[]; restCount: number; restSeconds: number } {
  if (rows.length <= n) return { shown: rows, restCount: 0, restSeconds: 0 };
  const rest = rows.slice(n);
  return {
    shown: rows.slice(0, n),
    restCount: rest.length,
    restSeconds: rest.reduce((sum, row) => sum + row.totalSeconds, 0),
  };
}

/** Saturday or Sunday, read off a `yyyy-MM-dd` key in UTC. */
export function isWeekendDayKey(date: string): boolean {
  const day = new Date(`${date}T00:00:00.000Z`).getUTCDay();
  if (Number.isNaN(day)) return false;
  return day === 0 || day === 6;
}

export const DELETED_PROJECT_LABEL = 'Deleted project';
export const FORMER_EMPLOYEE_LABEL = 'Former employee';

/**
 * A project or person deleted between the aggregation and the name lookup comes
 * back with empty display fields. The seconds are real and still count towards
 * the total, so the row stays — but it says what it is rather than rendering as
 * a nameless slice or a blank bar.
 */
function labelOr(name: string, fallback: string): string {
  const trimmed = name.trim();
  return trimmed === '' ? fallback : trimmed;
}

export function projectLabel(name: string): string {
  return labelOr(name, DELETED_PROJECT_LABEL);
}

export function memberLabel(name: string): string {
  return labelOr(name, FORMER_EMPLOYEE_LABEL);
}
